import React, { useEffect, useRef } from "react";
import "./blind-faith.css";
import { ASSETS, CALENDLY_URL, PRODUCTION_URL } from "../config";
import { usePageSeo, absoluteUrl, ORGANISATION_JSON_LD } from "../useSeo";
import { Header, BookACallLink } from "../Header";
import { FloatingCta, Footer } from "../FooterAndFloating";
import { BlindFaithStages, BlindFaithThemes, BlindFaithPractices } from "./BlindFaithSections";
import { EnquiryForm } from "../EnquiryForm";

const DESCRIPTION =
  "Could you convince a blind person that sight is real? Blind Faith is an interactive theatre experience exploring communication, critical thinking, trust, persuasion and leadership.";

const PAGE = {
  title: "Blind Faith | Interactive Leadership & Critical Thinking Experience | The Business Playwright",
  description: DESCRIPTION,
  path: "/blind-faith",
  ogImage: ASSETS.blindFaithPoster,
  ogImageAlt: "Blind Faith theatre poster",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Blind Faith",
    url: absoluteUrl("/blind-faith"),
    description: DESCRIPTION,
    primaryImageOfPage: absoluteUrl(ASSETS.blindFaithPoster),
    isPartOf: { "@type": "WebSite", name: "The Business Playwright", url: PRODUCTION_URL },
    publisher: ORGANISATION_JSON_LD,
    potentialAction: { "@type": "ReserveAction", target: CALENDLY_URL },
  },
};

const Scene = ({ id, title, dark, wide, children, testId }) => (
  <section className={`bp-scene bf-scene${dark ? " bf-dark" : ""}`} aria-labelledby={`${id}-title`} data-testid={testId}>
    <div className={`bp-wrap ${wide ? "" : "bf-column"}`}>
      <h2 id={`${id}-title`} className="bf-h2">{title}</h2>
      {children}
    </div>
  </section>
);

export default function BlindFaithPage() {
  usePageSeo(PAGE);
  const heroRef = useRef(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bp-site bf-page" data-testid="blind-faith-page">
      <Header />
      <main id="main">
        <section className="bf-hero" ref={heroRef} aria-labelledby="bf-hero-title" data-testid="bf-hero">
          <div className="bp-wrap bf-hero-grid">
            <div className="bf-hero-copy">
              <p className="bf-kicker" data-testid="bf-hero-kicker">BLIND FAITH</p>
              <h1 id="bf-hero-title" className="bf-hero-title" data-testid="bf-hero-title">Could you convince a blind person that sight is real?</h1>
              <p className="bf-hero-sub" data-testid="bf-hero-sub">An interactive theatre experience with powerful applications for business and leadership.</p>
              <div className="bf-body">
                <p>For his entire life, Jeff has been told he is blind.</p>
                <p>Now, he believes it was all a lie.</p>
                <p>In this darkly comedic interactive theatre experience, your team must attempt to convince Jeff there really is such a thing as sight before his growing conspiracy theory consumes him completely.</p>
                <p>But the more Jeff explains his reasoning, the harder it becomes to dismiss him outright.</p>
              </div>
              <div className="bf-questions" data-testid="bf-hero-questions">
                <p>Is sight real?</p>
                <p>How do we know what we know?</p>
                <p>And what happens when someone loses trust in reality itself?</p>
              </div>
              <BookACallLink className="bp-btn bf-btn" label="BOOK BLIND FAITH" testId="bf-hero-cta" />
            </div>
            <div className="bf-hero-poster">
              <img src={ASSETS.blindFaithPoster} alt="Blind Faith theatre poster" width="1122" height="1402" fetchPriority="high" decoding="async" data-testid="bf-hero-poster" />
            </div>
          </div>
        </section>

        <Scene id="bf-jeff" title="MEET JEFF" testId="bf-jeff">
          <div className="bf-body">
            <p>Jeff is intelligent, articulate and troubled.</p>
            <p>Once a successful corporate consultant who advised businesses on communication and leadership, he now spends his time obsessively trying to expose what he believes is one of humanity’s greatest deceptions.</p>
            <p>Raised in isolation by an overprotective mother, Jeff has spent his life relying entirely on the testimony of others.</p>
            <p>He has never knowingly experienced what most people call ‘seeing’, yet everyone insists sight is real.</p>
            <p>Recently, cracks have begun to appear in the story he was told.</p>
            <p>With difficulties building in his personal life, Jeff has reached a terrifying conclusion:</p>
          </div>
          <p className="bf-statement" data-testid="bf-jeff-statement">Perhaps nobody can see.</p>
          <div className="bf-body">
            <p>And if society could lie about something this fundamental, what else might be untrue?</p>
          </div>
        </Scene>

        <Scene id="bf-why" title="WHY THIS MATTERS" wide testId="bf-why">
          <div className="bf-why-grid">
            <div className="bf-body">
              <p>Blind Faith is designed to challenge participants psychologically, philosophically and emotionally.</p>
              <p>Through direct interaction with Jeff, your team is forced to confront:</p>
            </div>
            <BlindFaithThemes />
            <div className="bf-body">
              <p>The experience blends theatre, philosophy, psychology and dark comedy into something immediate, unpredictable and deeply personal.</p>
            </div>
            <p className="bf-emphasis" data-testid="bf-why-emphasis">Every performance becomes a unique conversation.</p>
          </div>
        </Scene>

        <Scene id="bf-inside" title="THIS ISN’T A PRESENTATION ABOUT CRITICAL THINKING" dark testId="bf-inside">
          <div className="bf-body">
            <p>Your team are placed inside the problem itself.</p>
          </div>
          <div className="bf-rhythm" data-testid="bf-inside-rhythm">
            <p>They have to listen to Jeff.</p>
            <p>Question him.</p>
            <p>Challenge his assumptions.</p>
            <p>Explain their own.</p>
          </div>
          <div className="bf-body">
            <p>And work out how you persuade someone whose view of reality is completely different from yours.</p>
            <p>As Jeff shares more about his own troubled life, his view of the world and his experiences of corporate culture, the conversation becomes increasingly difficult to dismiss as simply irrational.</p>
            <p>The result is a training experience that asks people to practise communication, listening, empathy and critical thinking rather than merely talk about them.</p>
          </div>
        </Scene>

        <Scene id="bf-how" title="HOW DOES IT WORK?" wide testId="bf-how">
          <BlindFaithStages />
        </Scene>

        <Scene id="bf-different" title="WHAT MAKES IT DIFFERENT?" testId="bf-different">
          <div className="bf-body">
            <p>Traditional training often asks people to discuss problems from a comfortable distance.</p>
          </div>
          <p className="bf-statement" data-testid="bf-different-statement">Blind Faith puts them inside one.</p>
          <div className="bf-body">
            <p>The experience is deliberately unsettling, funny, philosophical and unpredictable.</p>
            <p>Participants are not simply being told about:</p>
          </div>
          <BlindFaithPractices />
          <div className="bf-body">
            <p>Instead, they have to practice these things in real time.</p>
          </div>
        </Scene>

        <Scene id="bf-who" title="WHO’S BEHIND IT?" testId="bf-who">
          <div className="bf-body">
            <p>Created by Craig Wilmann, writing as Doe Wilmann.</p>
            <p>Craig is a playwright, storyteller and corporate facilitator who has spent more than a decade working with founders, CEOs and leadership groups.</p>
            <p>His work blends storytelling, philosophy, psychology and human behaviour.</p>
            <p>The role of Jeff will be performed by a blind or visually impaired actor who knows the character, the material and the structure of the experience in depth.</p>
          </div>
        </Scene>

        <Scene id="bf-where" title="A DIFFERENT KIND OF TRAINING EXPERIENCE" testId="bf-where">
          <div className="bf-body">
            <p>Blind Faith can work in theatres, meeting rooms, studios, conference spaces and other intimate environments.</p>
            <p>Your team can engage directly with Jeff or watch parts of the experience unfold before entering the conversation themselves.</p>
            <p>For larger audiences, a smaller group can interact directly with Jeff while the wider room participates through questions and responses.</p>
          </div>
        </Scene>

        <section className="bp-scene bf-scene bf-dark bf-final" aria-labelledby="bf-final-title" data-testid="bf-final">
          <div className="bp-wrap">
            <div className="bf-final-grid">
              <div className="bf-column-inner">
                <h2 id="bf-final-title" className="bf-h2">BRING BLIND FAITH TO YOUR BUSINESS</h2>
                <div className="bf-body">
                  <p>Challenge the way your team thinks, listens, communicates and responds to ideas they fundamentally disagree with.</p>
                  <p>And give them an experience they are unlikely to forget.</p>
                </div>
                <BookACallLink className="bp-btn bf-btn" label="BOOK A 30-MINUTE CALL" testId="bf-final-cta" />
              </div>
              <EnquiryForm production="blind-faith" prefix="bf-enquiry" buttonClass="bp-btn bf-btn" />
            </div>
            <p className="bf-final-lead">The core question remains:</p>
            <p className="bf-final-question" data-testid="bf-final-question">What do you actually know for certain?</p>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCta heroRef={heroRef} label="BOOK BLIND FAITH" testId="bf-floating-cta" />
    </div>
  );
}
